import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface VendorFormData {
  companyName: string;
  tradeName?: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  addressLine1?: string;
  city?: string;
  country?: string;
  taxId?: string;
  commissionRate: number;
  notes?: string;
}

interface VendorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendor?: {
    id: string;
    companyName: string;
    tradeName?: string | null;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    addressLine1?: string | null;
    city?: string | null;
    country?: string | null;
    taxId?: string | null;
    commissionRate?: number | null;
    notes?: string | null;
  } | null;
}

export function VendorModal({ open, onOpenChange, vendor }: VendorModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const utils = trpc.useUtils();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<VendorFormData>({
    defaultValues: {
      companyName: '',
      tradeName: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      addressLine1: '',
      city: '',
      country: '',
      taxId: '',
      commissionRate: 0,
      notes: '',
    },
  });

  useEffect(() => {
    if (vendor) {
      reset({
        companyName: vendor.companyName,
        tradeName: vendor.tradeName || '',
        contactName: vendor.contactName,
        contactEmail: vendor.contactEmail,
        contactPhone: vendor.contactPhone,
        addressLine1: vendor.addressLine1 || '',
        city: vendor.city || '',
        country: vendor.country || '',
        taxId: vendor.taxId || '',
        commissionRate: vendor.commissionRate || 0,
        notes: vendor.notes || '',
      });
    } else {
      reset({
        companyName: '',
        tradeName: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        addressLine1: '',
        city: '',
        country: '',
        taxId: '',
        commissionRate: 0,
        notes: '',
      });
    }
  }, [vendor, reset, open]);

  const createMutation = trpc.tradingConsignment.createVendor.useMutation({
    onSuccess: () => {
      showSuccessToast(t('consignment.vendors.vendorCreated'));
      utils.tradingConsignment.getVendors.invalidate();
      utils.tradingConsignment.listVendors.invalidate();
      onOpenChange(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const updateMutation = trpc.tradingConsignment.updateVendor.useMutation({
    onSuccess: () => {
      showSuccessToast(t('consignment.vendors.vendorUpdated'));
      utils.tradingConsignment.getVendors.invalidate();
      utils.tradingConsignment.listVendors.invalidate();
      onOpenChange(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const onSubmit = (data: VendorFormData) => {
    const { contactEmail, contactPhone, ...rest } = data;
    const payload = { ...rest, email: contactEmail, phone: contactPhone };
    if (vendor) {
      updateMutation.mutate({
        id: vendor.id,
        ...payload,
      });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {vendor ? t('consignment.vendors.editVendor') : t('consignment.vendors.addVendor')}
          </DialogTitle>
          <DialogDescription>
            {vendor
              ? t('consignment.vendors.editVendorDesc')
              : t('consignment.vendors.addVendorDesc')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Company Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">
              {t('consignment.vendors.companyInfo')}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="companyName">
                  {t('consignment.vendors.companyName')} *
                </Label>
                <Input
                  id="companyName"
                  {...register('companyName', { required: true })}
                  placeholder={t('consignment.vendors.companyNamePlaceholder')}
                />
                {errors.companyName && (
                  <p className="text-xs text-red-500">{tCommon('validation.required')}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="tradeName">{t('consignment.vendors.tradeName')}</Label>
                <Input
                  id="tradeName"
                  {...register('tradeName')}
                  placeholder={t('consignment.vendors.tradeNamePlaceholder')}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="taxId">{t('consignment.vendors.taxId')}</Label>
              <Input
                id="taxId"
                {...register('taxId')}
                placeholder={t('consignment.vendors.taxIdPlaceholder')}
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">
              {t('consignment.vendors.contactInfo')}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="contactName">
                  {t('consignment.vendors.contactName')} *
                </Label>
                <Input
                  id="contactName"
                  {...register('contactName', { required: true })}
                  placeholder={t('consignment.vendors.contactNamePlaceholder')}
                />
                {errors.contactName && (
                  <p className="text-xs text-red-500">{tCommon('validation.required')}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactPhone">
                  {t('consignment.vendors.phone')} *
                </Label>
                <Input
                  id="contactPhone"
                  {...register('contactPhone', { required: true })}
                  placeholder={t('consignment.vendors.phonePlaceholder')}
                />
                {errors.contactPhone && (
                  <p className="text-xs text-red-500">{tCommon('validation.required')}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactEmail">
                {t('consignment.vendors.email')} *
              </Label>
              <Input
                id="contactEmail"
                type="email"
                {...register('contactEmail', { required: true })}
                placeholder={t('consignment.vendors.emailPlaceholder')}
              />
              {errors.contactEmail && (
                <p className="text-xs text-red-500">{tCommon('validation.required')}</p>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">
              {t('consignment.vendors.address')}
            </h3>
            <div className="space-y-2">
              <Label htmlFor="addressLine1">{t('consignment.vendors.addressLine1')}</Label>
              <Input
                id="addressLine1"
                {...register('addressLine1')}
                placeholder={t('consignment.vendors.addressLine1Placeholder')}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="city">{t('consignment.vendors.city')}</Label>
                <Input
                  id="city"
                  {...register('city')}
                  placeholder={t('consignment.vendors.cityPlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">{t('consignment.vendors.country')}</Label>
                <Input
                  id="country"
                  {...register('country')}
                  placeholder={t('consignment.vendors.countryPlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* Commission */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">
              {t('consignment.vendors.financial')}
            </h3>
            <div className="space-y-2">
              <Label htmlFor="commissionRate">
                {t('consignment.vendors.commissionRate')}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="commissionRate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  {...register('commissionRate', { valueAsNumber: true })}
                  className="w-32"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('consignment.vendors.commissionRateHint')}
              </p>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">{t('consignment.vendors.notes')}</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder={t('consignment.vendors.notesPlaceholder')}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {vendor ? tCommon('save') : tCommon('create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
